/* ============================================================
   TRADIE — Colour mixer tiles: mouse drag + wheel scroll + arrows
   Pairs with colour-mixer-tiles-carousel.css, which turned
   .pf-colmix-tiles from a large grid into a small horizontally-
   scrolling row. Native overflow-x:auto only responds to touch
   drags and trackpad/shift+wheel — a plain mouse wheel does nothing
   on a horizontal-only container, and there's no visible scrollbar
   (hidden on purpose to match the smaller Step One-style look) or
   arrow buttons on this particular row, so with more tiles than fit
   on screen there was no way to reach the rest of them.

   Reuses the exact same drag-to-scroll + wheel-to-horizontal pattern
   already shipped in assets/pf-variant-picker.js for the colour
   swatch carousel (.pf-color-variant-wrapper) — same approach, just
   scoped to .pf-colmix-tiles instead. Suppresses the next click
   after an actual drag so dragging across a tile doesn't
   accidentally fire its remove (×) button.

   Adds visible prev/next arrow buttons too (user feedback: the lack
   of a scrollbar/arrows made it unclear the row could be scrolled at
   all). snippets/pf-color-mixer.liquid is app-owned/regenerated, so
   the wrapper + buttons are injected here rather than hand-edited
   into that snippet — same approach used for the "Size chart" label
   in size-selector-reposition.js. Styled/behaves like the existing
   swatch-picker carousel arrows in pf-variant-picker.js
   (.pf-color-carousel__arrow / pfUpdateCarouselArrows) for
   consistency: hidden when there's nothing to scroll to on that
   side, shown otherwise.
   ============================================================ */

(function () {
  const CAROUSEL_SELECTOR = ".pf-colmix-tiles";

  document.addEventListener("wheel", (e) => {
    const carousel = e.target.closest(CAROUSEL_SELECTOR);
    if (!carousel) return;
    if (carousel.scrollWidth <= carousel.clientWidth) return;

    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      carousel.scrollLeft += e.deltaY;
      e.preventDefault();
    }
  }, { passive: false });

  let dragState = null;

  document.addEventListener("mousedown", (e) => {
    const carousel = e.target.closest(CAROUSEL_SELECTOR);
    if (!carousel) return;

    dragState = {
      carousel,
      startX: e.pageX,
      startScrollLeft: carousel.scrollLeft,
      moved: false,
    };
    carousel.classList.add("is-dragging");
  });

  document.addEventListener("mousemove", (e) => {
    if (!dragState) return;

    const dx = e.pageX - dragState.startX;
    if (Math.abs(dx) > 3) dragState.moved = true;

    dragState.carousel.scrollLeft = dragState.startScrollLeft - dx;
  });

  const endDrag = () => {
    if (!dragState) return;
    dragState.carousel.classList.remove("is-dragging");

    if (dragState.moved) {
      const carousel = dragState.carousel;
      const suppressClick = (ev) => {
        ev.stopPropagation();
        ev.preventDefault();
        carousel.removeEventListener("click", suppressClick, true);
      };
      carousel.addEventListener("click", suppressClick, true);
    }

    dragState = null;
  };

  document.addEventListener("mouseup", endDrag);
  document.addEventListener("mouseleave", endDrag);
})();

// ===================
// Tiles carousel: prev/next arrow buttons
// ===================
(function () {
  const TILES_SELECTOR = ".pf-colmix-tiles";
  const WRAPPER_CLASS = "pf-colmix-carousel";

  function svgChevron(direction) {
    const d = direction === "prev" ? "M15 6l-6 6 6 6" : "M9 6l6 6-6 6";
    return `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="${d}" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  }

  function makeArrow(direction) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = `${WRAPPER_CLASS}__arrow ${WRAPPER_CLASS}__arrow--${direction}`;
    btn.setAttribute(direction === "prev" ? "data-colmix-prev" : "data-colmix-next", "");
    btn.setAttribute("aria-label", direction === "prev" ? "Scroll bundle items left" : "Scroll bundle items right");
    btn.hidden = true;
    btn.innerHTML = svgChevron(direction);
    return btn;
  }

  function ensureColmixArrows() {
    document.querySelectorAll(TILES_SELECTOR).forEach((tiles) => {
      if (tiles.parentNode && tiles.parentNode.classList && tiles.parentNode.classList.contains(WRAPPER_CLASS)) {
        return;
      }

      const wrapper = document.createElement("div");
      wrapper.className = WRAPPER_CLASS;

      tiles.parentNode.insertBefore(wrapper, tiles);
      wrapper.appendChild(makeArrow("prev"));
      wrapper.appendChild(tiles);
      wrapper.appendChild(makeArrow("next"));
    });
  }

  function updateColmixArrows(tiles) {
    const wrapper = tiles.closest(`.${WRAPPER_CLASS}`);
    if (!wrapper) return;

    const prevBtn = wrapper.querySelector("[data-colmix-prev]");
    const nextBtn = wrapper.querySelector("[data-colmix-next]");

    const hasOverflow = tiles.scrollWidth > tiles.clientWidth + 2;
    const atStart = tiles.scrollLeft <= 2;
    const atEnd = tiles.scrollLeft >= tiles.scrollWidth - tiles.clientWidth - 2;

    if (prevBtn) prevBtn.hidden = !hasOverflow || atStart;
    if (nextBtn) nextBtn.hidden = !hasOverflow || atEnd;
  }

  function updateAllColmixArrows() {
    document.querySelectorAll(TILES_SELECTOR).forEach(updateColmixArrows);
  }

  function scrollColmixTiles(tiles, direction) {
    const tile = tiles.querySelector(".pf-colmix-tile");
    const step = tile ? tile.getBoundingClientRect().width + 10 : 90;

    tiles.scrollBy({ left: direction * step * 3, behavior: "smooth" });
  }

  document.addEventListener("click", (e) => {
    const prevBtn = e.target.closest("[data-colmix-prev]");
    const nextBtn = e.target.closest("[data-colmix-next]");
    if (!prevBtn && !nextBtn) return;

    const wrapper = (prevBtn || nextBtn).closest(`.${WRAPPER_CLASS}`);
    const tiles = wrapper && wrapper.querySelector(TILES_SELECTOR);
    if (!tiles) return;

    scrollColmixTiles(tiles, prevBtn ? -1 : 1);
  });

  document.addEventListener("scroll", (e) => {
    const target = e.target;
    if (!target || !target.classList || !target.classList.contains("pf-colmix-tiles")) return;
    updateColmixArrows(target);
  }, true);

  window.addEventListener("resize", updateAllColmixArrows);
  window.addEventListener("load", updateAllColmixArrows);

  document.addEventListener("DOMContentLoaded", () => {
    ensureColmixArrows();
    updateAllColmixArrows();
  });

  // Re-run when tiles are added/removed (picking colours, adding
  // pairs) or the product info column re-renders on a colour switch.
  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      ensureColmixArrows();
      updateAllColmixArrows();
    });
  });

  document.addEventListener("DOMContentLoaded", () => {
    observer.observe(document.body, { childList: true, subtree: true });
  });
})();
